/**
 * src/three/SoundFx.js
 * เสียงยิง / โดน / พัง / เบสค้างยิง / ไฟนอล
 */
import { T3 } from './config.js';
import { AUDIO, fetchAudioBuffer } from './audioFiles.js';
import { decodeAudioData, getAudioContext, resumeAudioContext } from './audioContext.js';
const SHOOT_VOICES = 5;
const BREAK_VOICES = 4;
const FACE_VOICES = 4;
export default class SoundFx {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.enabled = true;
    this.bassGain = null;
    this.bassOsc = null;
    this.bassOn = false;
    this._finaleMuteUntil = 0;
    this._lastVibrate = 0;
    this._iosSwitch = null;
    this._tickLabel = null;
    this._tickInput = null;
    this._pending = 0;
    this._armed = false;
    this._resuming = false;
    this._blocked = false;
    this._bassIntensity = null;
    this.userVolume = 1;
    this._masterBase = 0.35;
    this._shootBuf = null;
    this._shootLoad = null;
    this._shootVoices = [];
    this._breakBuf = null;
    this._breakLoad = null;
    this._breakVoices = [];
    this._faceBuf = null;
    this._faceLoad = null;
    this._faceVoices = [];
  }

  canVibrate() {
    return typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';
  }

  isIos() {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    if (/iPhone|iPad|iPod/i.test(ua)) return true;
    return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  }

  setIosSwitch(el) {
    this._iosSwitch = el || null;
  }

  ensureIosTick() {
    if (this._tickLabel?.isConnected) return;
    if (typeof document === 'undefined') return;
    const label = document.createElement('label');
    label.setAttribute('aria-hidden', 'true');
    label.style.cssText = 'position:fixed;left:0;top:0;width:32px;height:32px;opacity:0.02;pointer-events:none;z-index:-1;';
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.setAttribute('switch', '');
    input.tabIndex = -1;
    label.appendChild(input);
    document.body.appendChild(label);
    this._tickLabel = label;
    this._tickInput = input;
  }

  /** ปลดล็อกสั่นตอนนิ้วแตะจริง (Android ต้องมี user gesture) */
  armHaptics() {
    this._armed = true;
    if (this.canVibrate() && !this.isIos()) {
      try {
        navigator.vibrate(1);
      } catch {
        /* ignore */
      }
      return;
    }
    this.tickIos();
  }

  tickIos() {
    try {
      this.ensureIosTick();
      if (this._iosSwitch?.isConnected) this._iosSwitch.click();
      this._tickLabel?.click();
    } catch {
      /* ignore */
    }
  }

  /** ดึงคิวสั่น iOS ตอน pointer ยังกดค้าง */
  flushHaptics() {
    if (this._pending <= 0) return;
    const n = Math.min(this._pending, 3);
    this._pending = 0;
    for (let i = 0; i < n; i += 1) {
      if (i === 0) this.tickIos();
      else window.setTimeout(() => this.tickIos(), i * 45);
    }
  }

  hapticIos(times = 1) {
    this._pending += Math.max(1, times);
  }

  /** สั่นมือถือ — Android = vibrate, iPhone = Taptic ผ่าน switch */
  vibrate(pattern) {
    if (this.canVibrate() && !this.isIos()) {
      try {
        navigator.vibrate(pattern);
      } catch {
        /* ignore */
      }
      return;
    }
    this.hapticIos(1);
  }

  /** สั่น 1 ติ๊กต่อกล่อง — เรียกตอนยิงโดน/พัง */
  pulseBreak() {
    const now = performance.now();
    if (now - this._lastVibrate < 60) return;
    this._lastVibrate = now;
    if (this.canVibrate() && !this.isIos()) {
      try {
        navigator.vibrate(40);
      } catch {
        /* ignore */
      }
      return;
    }
    this._pending += 1;
  }

  vibrateBreak(combo = 1, count = 1) {
    const n = Math.max(1, Math.min(8, count));
    for (let i = 0; i < n; i += 1) {
      if (i === 0) this.pulseBreak();
      else window.setTimeout(() => this.pulseBreak(), i * 48);
    }
  }

  vibrateFinale() {
    this._lastVibrate = 0;
    if (this.canVibrate() && !this.isIos()) {
      this.vibrate(T3.VIBRATE_FINALE || [50, 40, 90]);
      return;
    }
    this._pending += 3;
    this.flushHaptics();
  }

  ensure() {
    if (this.ctx && this.master) return;
    this.ctx = getAudioContext();
    if (!this.ctx) return;
    this.master = this.ctx.createGain();
    this.master.gain.value = this._masterBase * this.userVolume;
    this.master.connect(this.ctx.destination);

    this.bassGain = this.ctx.createGain();
    this.bassGain.gain.value = 0;
    this.bassOsc = this.ctx.createOscillator();
    this.bassOsc.type = 'sine';
    this.bassOsc.frequency.value = 48;
    this.bassOsc.connect(this.bassGain);
    this.bassGain.connect(this.master);
    this.bassOsc.start();
    this._ensureNoise();
    this._loadShoot();
    this._loadBreak();
    this._loadFace();
  }

  _loadShoot() {
    if (this._shootLoad) return this._shootLoad;
    if (!this.ctx) return Promise.resolve(null);
    this._shootLoad = fetchAudioBuffer(AUDIO.shoot)
      .then((data) => decodeAudioData(this.ctx, data))
      .then((buf) => {
        this._shootBuf = buf;
        return buf;
      })
      .catch(() => null);
    return this._shootLoad;
  }

  _loadBreak() {
    if (this._breakLoad) return this._breakLoad;
    if (!this.ctx) return Promise.resolve(null);
    this._breakLoad = fetchAudioBuffer(AUDIO.break)
      .then((data) => decodeAudioData(this.ctx, data))
      .then((buf) => {
        this._breakBuf = buf;
        return buf;
      })
      .catch(() => null);
    return this._breakLoad;
  }

  _loadFace() {
    if (this._faceLoad) return this._faceLoad;
    if (!this.ctx) return Promise.resolve(null);
    this._faceLoad = fetchAudioBuffer(AUDIO.face)
      .then((data) => decodeAudioData(this.ctx, data))
      .then((buf) => {
        this._faceBuf = buf;
        return buf;
      })
      .catch(() => null);
    return this._faceLoad;
  }

  /** ปลดล็อกเสียงตอนมี user gesture — อย่าเรียกจาก game loop */
  unlock() {
    this._blocked = false;
    this.resume();
  }

  resume() {
    this.ensure();
    if (!this.ctx) return;
    resumeAudioContext().then(() => {
      this._blocked = this.ctx?.state !== 'running';
    });
  }

  muted() {
    return !this.enabled || (this.ctx && this.ctx.currentTime < this._finaleMuteUntil);
  }

  applyMaster() {
    if (!this.ctx || !this.master) return;
    const t = this.ctx.currentTime;
    const target = this.enabled ? this._masterBase * this.userVolume : 0;
    this.master.gain.cancelScheduledValues(t);
    this.master.gain.setTargetAtTime(target, t, 0.05);
    if (!this.enabled && this.bassGain) {
      this.bassGain.gain.cancelScheduledValues(t);
      this.bassGain.gain.setTargetAtTime(0, t, 0.05);
      this.bassOn = false;
    }
  }

  getUserVolume() {
    return this.userVolume;
  }

  setUserVolume(v) {
    this.userVolume = Math.max(0, Math.min(1, Number(v) || 0));
    this.enabled = this.userVolume > 0.001;
    this.ensure();
    this.applyMaster();
  }

  setEnabled(on) {
    this.enabled = !!on;
    this.ensure();
    this.applyMaster();
  }

  toggle() {
    this.setEnabled(!this.enabled);
    return this.enabled;
  }

  /** เบสทุ้มตอนกดยิงค้าง — ไม่สร้าง/resume AudioContext จาก loop */
  setFiringBass(on, intensity = 1) {
    if (!this.enabled) on = false;
    on = !!on;
    if (!this.ctx || this.ctx.state !== 'running' || !this.bassGain) {
      this.bassOn = false;
      this._bassIntensity = null;
      return;
    }
    if (this.bassOn === on && this._bassIntensity === intensity) return;
    const t = this.ctx.currentTime;
    const target = on ? 0.12 * intensity : 0;
    this.bassGain.gain.cancelScheduledValues(t);
    this.bassGain.gain.setTargetAtTime(target, t, 0.08);
    if (this.bassOsc) {
      this.bassOsc.frequency.setTargetAtTime(on ? 42 + intensity * 18 : 48, t, 0.1);
    }
    this.bassOn = on;
    this._bassIntensity = intensity;
  }

  playShoot(berserk = false) {
    if (this.muted()) return;
    this.resume();
    if (!this.ctx) return;
    if (this._shootBuf) {
      this._playShootSample(berserk);
      return;
    }
    this._loadShoot();
    this._playShootSynth(berserk);
  }

  _playShootSample(berserk = false) {
    if (!this.ctx || !this._shootBuf) return;
    const t = this.ctx.currentTime;
    while (this._shootVoices.length >= SHOOT_VOICES) {
      const old = this._shootVoices.shift();
      try { old.stop(); } catch { /* ignore */ }
    }
    const src = this.ctx.createBufferSource();
    src.buffer = this._shootBuf;
    src.playbackRate.value = (berserk ? 1.12 : 1) * (0.97 + Math.random() * 0.06);
    const g = this.ctx.createGain();
    const peak = berserk ? 0.7 : 0.56;
    const dur = Math.min(0.42, this._shootBuf.duration);
    g.gain.setValueAtTime(peak, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(g);
    g.connect(this.master);
    src.start(t, 0, dur + 0.02);
    src.onended = () => {
      this._shootVoices = this._shootVoices.filter((s) => s !== src);
    };
    this._shootVoices.push(src);
  }

  _playShootSynth(berserk = false) {
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = berserk ? 'sawtooth' : 'square';
    osc.frequency.setValueAtTime((berserk ? 220 : 180) + Math.random() * 40, t);
    osc.frequency.exponentialRampToValueAtTime(berserk ? 90 : 60, t + 0.06);
    g.gain.setValueAtTime(berserk ? 0.55 : 0.45, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.08);
    osc.connect(g);
    g.connect(this.master);
    osc.start(t);
    osc.stop(t + 0.09);
    this.noiseBurst(0.05, berserk ? 0.38 : 0.28, berserk ? 1200 : 900, t);
  }

  /** คลิกแม็กว่าง */
  playEmpty() {
    if (this.muted()) return;
    this.resume();
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = 'square';
    osc.frequency.setValueAtTime(90, t);
    osc.frequency.exponentialRampToValueAtTime(40, t + 0.04);
    g.gain.setValueAtTime(0.18, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
    osc.connect(g);
    g.connect(this.master);
    osc.start(t);
    osc.stop(t + 0.06);
  }

  /** ยิงโดนอิฐที่มีหน้า */
  playFaceHit() {
    if (this.muted()) return;
    this.resume();
    if (!this.ctx) return;
    if (this._faceBuf) {
      this._playFaceSample();
      return;
    }
    this._loadFace();
  }

  _playFaceSample() {
    if (!this.ctx || !this._faceBuf) return;
    const t = this.ctx.currentTime;
    while (this._faceVoices.length >= FACE_VOICES) {
      const old = this._faceVoices.shift();
      try { old.stop(); } catch { /* ignore */ }
    }
    const src = this.ctx.createBufferSource();
    src.buffer = this._faceBuf;
    src.playbackRate.value = 0.97 + Math.random() * 0.08;
    const g = this.ctx.createGain();
    const peak = 2.2;
    const dur = Math.min(0.38, this._faceBuf.duration);
    g.gain.setValueAtTime(peak, t);
    g.gain.setValueAtTime(peak, t + 0.06);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(g);
    g.connect(this.master);
    src.start(t, 0, dur);
    src.onended = () => {
      this._faceVoices = this._faceVoices.filter((s) => s !== src);
    };
    this._faceVoices.push(src);
  }

  /** เสียงพัง — ดังขึ้นตามคอมโบ */
  playBreak(combo = 1) {
    if (this.muted()) return;
    this.resume();
    if (!this.ctx) return;
    if (this._breakBuf) {
      this._playBreakSample(combo);
      return;
    }
    this._loadBreak();
    this._playBreakSynth(combo);
  }

  _playBreakSample(combo = 1, { finale = false } = {}) {
    if (!this.ctx || !this._breakBuf) return;
    const t = this.ctx.currentTime;
    while (this._breakVoices.length >= BREAK_VOICES) {
      const old = this._breakVoices.shift();
      try { old.stop(); } catch { /* ignore */ }
    }
    const src = this.ctx.createBufferSource();
    src.buffer = this._breakBuf;
    const boost = Math.min(2, 1 + combo * 0.06);
    src.playbackRate.value = (finale ? 0.92 : 1) * (0.96 + Math.random() * 0.08);
    const g = this.ctx.createGain();
    const peak = (finale ? 3.2 : 2.6) * boost;
    const dur = Math.min(finale ? this._breakBuf.duration : 1.05, this._breakBuf.duration);
    g.gain.setValueAtTime(peak, t);
    g.gain.setValueAtTime(peak, t + 0.12);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(g);
    g.connect(this.master);
    src.start(t, 0, dur);
    src.onended = () => {
      this._breakVoices = this._breakVoices.filter((s) => s !== src);
    };
    this._breakVoices.push(src);
  }

  _playBreakSynth(combo = 1) {
    const t = this.ctx.currentTime;
    const boost = Math.min(2, 1 + combo * 0.06);
    this.noiseBurst(0.14 + combo * 0.004, 0.5 * boost, 400 - Math.min(120, combo * 4), t);

    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(90 + combo * 2, t);
    osc.frequency.exponentialRampToValueAtTime(28, t + 0.22);
    g.gain.setValueAtTime(0.25 * boost, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
    osc.connect(g);
    g.connect(this.master);
    osc.start(t);
    osc.stop(t + 0.26);

    if (combo >= 8) {
      const sub = this.ctx.createOscillator();
      const sg = this.ctx.createGain();
      sub.type = 'sine';
      sub.frequency.setValueAtTime(55, t);
      sub.frequency.exponentialRampToValueAtTime(28, t + 0.35);
      sg.gain.setValueAtTime(0.2, t);
      sg.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
      sub.connect(sg);
      sg.connect(this.master);
      sub.start(t);
      sub.stop(t + 0.42);
    }
  }

  /** เงียบครู่หนึ่งตอนกำแพงสุดท้ายพัง */
  playFinale() {
    this.resume();
    if (!this.ctx) return;
    this.setFiringBass(false);
    const t = this.ctx.currentTime;
    this._finaleMuteUntil = t + 0.55;

    // ทุ้มก้องหนึ่งที แล้วเงียบ
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(70, t);
    osc.frequency.exponentialRampToValueAtTime(22, t + 0.5);
    g.gain.setValueAtTime(0.4, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.55);
    osc.connect(g);
    g.connect(this.master);
    osc.start(t);
    osc.stop(t + 0.56);

    this.noiseBurst(0.35, 0.55, 180, t);
    if (this._breakBuf) this._playBreakSample(8, { finale: true });
    else this._loadBreak();
  }

  playBerserkOn() {
    if (!this.enabled) return;
    this.resume();
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(80, t);
    osc.frequency.exponentialRampToValueAtTime(220, t + 0.25);
    g.gain.setValueAtTime(0.28, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
    osc.connect(g);
    g.connect(this.master);
    osc.start(t);
    osc.stop(t + 0.36);
  }

  playTargetPop() {
    if (this.muted()) return;
    this.resume();
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(320, t);
    osc.frequency.exponentialRampToValueAtTime(90, t + 0.2);
    g.gain.setValueAtTime(0.22, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.22);
    osc.connect(g);
    g.connect(this.master);
    osc.start(t);
    osc.stop(t + 0.23);
  }

  _ensureNoise() {
    if (this._noiseBuf || !this.ctx) return;
    const n = Math.floor(this.ctx.sampleRate * 0.4);
    const buffer = this.ctx.createBuffer(1, n, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < n; i++) data[i] = Math.random() * 2 - 1;
    this._noiseBuf = buffer;
  }

  noiseBurst(duration, volume, filterFreq, when) {
    this._ensureNoise();
    if (!this._noiseBuf) return;
    const src = this.ctx.createBufferSource();
    src.buffer = this._noiseBuf;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = filterFreq;
    filter.Q.value = 0.8;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(volume, when);
    g.gain.exponentialRampToValueAtTime(0.001, when + duration);
    src.connect(filter);
    filter.connect(g);
    g.connect(this.master);
    src.start(when);
    src.stop(when + duration);
  }
}
